import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const FILTER_SEARCH_DEBOUNCE_MS = 300;

type FilterOptions<K extends string> = {
  debounceKeys?: readonly NoInfer<K>[];
  allowed?: Partial<Record<NoInfer<K>, readonly string[]>>;
};

function readFilters<K extends string>(
  params: URLSearchParams,
  defaults: Record<K, string>,
  allowed: Partial<Record<K, readonly string[]>> | undefined,
): Record<K, string> {
  const values = { ...defaults };
  for (const key of Object.keys(defaults) as K[]) {
    const raw = params.get(key);
    if (raw == null || raw === "") continue;
    const allow = allowed?.[key];
    if (allow && !allow.includes(raw)) continue;
    values[key] = raw;
  }
  return values;
}

function isOwnedDefault<K extends string>(
  key: K,
  raw: string,
  defaults: { [P in K]: string },
  allowed: Partial<Record<K, readonly string[]>> | undefined,
): boolean {
  if (raw === "" || raw === defaults[key]) return true;
  const allow = allowed?.[key];
  return Boolean(allow && !allow.includes(raw));
}

export function useQueryFilters<K extends string>(
  defaults: { [P in K]: string },
  options?: FilterOptions<K>,
) {
  const [params, setParams] = useSearchParams();
  const allowed = options?.allowed;
  const debounceKeysKey = (options?.debounceKeys ?? []).join("|");

  const values = useMemo(
    () => readFilters(params, defaults, allowed),
    [params, defaults, allowed],
  );
  const [draft, setDraft] = useState(values);
  const prevValuesRef = useRef(values);
  const timersRef = useRef<Partial<Record<K, number>>>({});

  const debounceKeys = useMemo(
    () => new Set((debounceKeysKey ? debounceKeysKey.split("|") : []) as K[]),
    [debounceKeysKey],
  );

  const write = useCallback(
    (patch: Partial<Record<K, string>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(patch) as K[]) {
            if (!(key in defaults)) continue;
            const value = patch[key];
            if (value == null || isOwnedDefault(key, value, defaults, allowed)) {
              next.delete(key);
              continue;
            }
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [allowed, defaults, setParams],
  );

  useEffect(() => {
    const timers = timersRef.current;
    const onPopState = () => {
      for (const key of Object.keys(timers) as K[]) {
        const timer = timers[key];
        if (timer != null) window.clearTimeout(timer);
        delete timers[key];
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      for (const timer of Object.values(timers) as Array<number | undefined>) {
        if (timer != null) window.clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    const hasStale = (Object.keys(defaults) as K[]).some((key) => {
      if (!params.has(key)) return false;
      return isOwnedDefault(key, params.get(key) ?? "", defaults, allowed);
    });
    if (!hasStale) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of Object.keys(defaults) as K[]) {
          if (!next.has(key)) continue;
          if (isOwnedDefault(key, next.get(key) ?? "", defaults, allowed)) next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }, [allowed, defaults, params, setParams]);

  useEffect(() => {
    const previous = prevValuesRef.current;
    prevValuesRef.current = values;
    setDraft((current) => {
      let changed = false;
      const next = { ...current };
      for (const key of Object.keys(values) as K[]) {
        if (previous[key] === values[key]) continue;
        if (timersRef.current[key] != null) continue;
        if (current[key] === values[key]) continue;
        next[key] = values[key];
        changed = true;
      }
      return changed ? next : current;
    });
  }, [values]);

  const set = useCallback(
    (patch: Partial<Record<K, string>>) => {
      const immediate: Partial<Record<K, string>> = {};
      setDraft((current) => ({ ...current, ...patch }));
      for (const key of Object.keys(patch) as K[]) {
        if (!(key in defaults)) continue;
        const value = patch[key];
        if (value === undefined) continue;
        if (debounceKeys.has(key)) {
          const pending = timersRef.current[key];
          if (pending != null) window.clearTimeout(pending);
          timersRef.current[key] = window.setTimeout(() => {
            delete timersRef.current[key];
            write({ [key]: value } as Partial<Record<K, string>>);
          }, FILTER_SEARCH_DEBOUNCE_MS);
        } else {
          immediate[key] = value;
        }
      }
      if (Object.keys(immediate).length > 0) write(immediate);
    },
    [debounceKeys, defaults, write],
  );

  const clear = useCallback(
    (keys?: readonly K[]) => {
      const targets = (keys ?? (Object.keys(defaults) as K[])).filter((key) => key in defaults);
      for (const key of targets) {
        const pending = timersRef.current[key];
        if (pending != null) window.clearTimeout(pending);
        delete timersRef.current[key];
      }
      setDraft((current) => {
        const next = { ...current };
        for (const key of targets) next[key] = defaults[key];
        return next;
      });
      write(
        Object.fromEntries(targets.map((key) => [key, defaults[key]])) as Partial<Record<K, string>>,
      );
    },
    [defaults, write],
  );

  return { values, draft, set, clear };
}
